import { useState, useEffect } from 'react';
import { C } from '@parsina/framework/tokens';
import { Toggle, NumberInput, Button } from '@parsina/framework';
import { useDoctor } from '@parsina/framework/clinical/src/hooks/useDoctor';
import { useWalletBalance } from '@parsina/framework/clinical/src/hooks/useWalletBalance';
import { useLowBalanceConfig } from '../../hooks/useLowBalanceConfig';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { creditLevel, creditColor, CREDIT_LEVEL_META, CREDIT_LOW } from '@parsina/framework/clinical/src/constants/credit';
import { fmtCoins } from '@parsina/framework/clinical/src/constants/creditPricing';
import { type } from '@parsina/framework/clinical/src/constants/typography';
import { CoinGlyph } from '@parsina/framework/clinical/src/components/common/CoinChip';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { useCreditPurchase } from '../../hooks/useCreditPurchase';

// The «اعتبار» settings section — configuration only (the report lives on
// /credits). A balance summary with entry points, plus the low-balance alert
// config wired to the wallet's lowBalanceConfig / updateLowBalanceThreshold.
export default function SettingsCreditsPanel({ isMobile = false }) {
  const { t, formatNumber } = useTranslation();
  const { doctor } = useDoctor();
  const navigate = useAppNavigate();
  const balance = useWalletBalance(doctor?.id);
  const { config, loading, saving, save } = useLowBalanceConfig(doctor?.id);
  const { canBuy } = useCreditPurchase();

  const [enableAlerts, setEnableAlerts] = useState(true);
  const [threshold, setThreshold] = useState(String(CREDIT_LOW * 4));
  const [savedTick, setSavedTick] = useState(false);

  useEffect(() => {
    if (config) {
      setEnableAlerts(config.enableAlerts);
      setThreshold(String(Math.round(config.threshold)));
    }
  }, [config]);

  const dirty = config && (config.enableAlerts !== enableAlerts || Math.round(config.threshold) !== Number(threshold));
  const level = creditLevel(balance);

  const onSave = async () => {
    const ok = await save({ enableAlerts, threshold: Math.max(CREDIT_LOW, Number(threshold) || CREDIT_LOW) });
    if (ok) { setSavedTick(true); setTimeout(() => setSavedTick(false), 2000); }
  };

  const card = {
    background: C.card, border: `1px solid ${C.border}`, borderRadius: 14,
    padding: isMobile ? 14 : 18, boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 640 }}>

      {/* Balance summary + entry points */}
      <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 140 }}>
          <div style={{ ...type.caption, color: C.textMuted }}>{t('credits.currentBalance')}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
            <span style={{ color: creditColor(balance), display: 'flex' }}><CoinGlyph size={16} /></span>
            <span style={{ ...type.metric, color: C.textPrimary }}>{balance === null ? '—' : fmtCoins(balance, formatNumber)}</span>
            <span style={{ ...type.unit, fontWeight: 600, color: C.textMuted }}>{t('common.units.coin')}</span>
            {balance !== null && (
              <span style={{ ...type.caption, fontWeight: 700, color: CREDIT_LEVEL_META[level].color, background: CREDIT_LEVEL_META[level].tint, borderRadius: 999, padding: '2px 9px' }}>
                {t(CREDIT_LEVEL_META[level].labelKey)}
              </span>
            )}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate('credits')} data-testid="settings-credits-view">{t('credits.viewTransactions')}</Button>
        {/* Buying credit only where it is sold and this panel has a rail for its
            price (hooks/useCreditPurchase.js, docs/platform-money/CONTRACT.md). */}
        {canBuy && (
          <Button size="sm" onClick={() => navigate('credits')} data-testid="settings-credits-topup">{t('credits.buyCoins')}</Button>
        )}
      </div>

      {/* Low-balance alert config */}
      <div style={card}>
        <h2 style={{ ...type.title, color: C.textPrimary, margin: '0 0 4px' }}>{t('credits.lowBalanceTitle')}</h2>
        <div style={{ ...type.label, color: C.textMuted, marginBottom: 14 }}>
          {t('credits.lowBalanceHint')}
        </div>

        <Toggle
          id="settings-credits-alerts"
          data-testid="settings-credits-alerts"
          label={t('settings.credits.thresholdAlert')}
          checked={enableAlerts}
          onChange={setEnableAlerts}
          disabled={loading}
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, opacity: enableAlerts ? 1 : 0.5 }}>
          <div style={{ flex: 1 }}>
            <div style={{ ...type.label, color: C.textPrimary }}>{t('credits.thresholdLabel')}</div>
            <div style={{ ...type.micro, fontWeight: 600, color: C.textMuted, marginTop: 2 }}>{t('credits.thresholdMin', { amount: fmtCoins(CREDIT_LOW, formatNumber) })}</div>
          </div>
          <div style={{ width: 120 }}>
            <NumberInput
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              min={CREDIT_LOW}
              disabled={!enableAlerts || loading}
              aria-label={t('settings.credits.threshold')}
              data-testid="settings-credits-threshold"
            />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16 }}>
          <Button onClick={onSave} disabled={!dirty || saving} data-testid="settings-credits-save">
            {saving ? t('common.state.saving') : t('common.actions.save')}
          </Button>
          {savedTick && <span style={{ ...type.label, fontWeight: 700, color: C.green }}>{t('common.state.saved')} ✓</span>}
        </div>
      </div>
    </div>
  );
}
